import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  AccountBalanceOutlined,
  ArrowForwardOutlined,
  TrendingUpOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Link as RouterLink } from 'react-router-dom';
import { formatCurrency, formatCurrencyCompact } from '../../../utils/CurrencyFormat';
import {
  getCapitalAnalyticsSummary,
  listCapitalMarginPeriods,
  listCapitalMaturityWarnings,
  listPartnerCapitalDeployments,
} from '../../../services/request/CapitalAnalytics';
import type {
  CapitalAnalyticsSummary,
  MarginGranularity,
  MarginPeriod,
  MaturitySeverity,
  MaturityWarning,
  PartnerCapitalDeployment,
} from '../../../models/CapitalAnalyticsTypes';

const STOCK_PICKS_PATH = '/admin/capitals/stock-picks';
const fmt = (n: number) => formatCurrency(n, 'INR', true);
const fmtCompact = (n: number) => formatCurrencyCompact(n, 'INR', true);

const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  caption?: React.ReactNode;
  accent?: 'primary' | 'success' | 'warning' | 'info' | 'error';
}> = ({ label, value, caption, accent }) => {
  const colors = { primary: '#1a237e', success: '#15803d', warning: '#b45309', info: '#1e40af', error: '#c62828' };
  return (
    <Card sx={{ height: '100%', borderLeft: accent ? 4 : undefined, borderLeftColor: accent ? colors[accent] : undefined }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary" sx={{ fontSize: 11 }}>{label}</Typography>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>{value}</Typography>
        {caption && <Typography variant="caption" color="text.secondary">{caption}</Typography>}
      </CardContent>
    </Card>
  );
};

const TAB_LABELS = ['Deployment', 'Margin report', 'Maturity warnings'] as const;

function severityTone(severity: MaturitySeverity): 'error' | 'warning' | 'info' {
  switch (severity) {
    case 'CRITICAL':
      return 'error';
    case 'WARNING':
      return 'warning';
    default:
      return 'info';
  }
}

function kindLabel(kind: MaturityWarning['kind']): string {
  switch (kind) {
    case 'LENDING_DEBIT':
      return 'Lending debit';
    case 'LOCK_IN':
      return 'Lock-in';
    case 'SCHEME_TENURE':
      return 'Scheme tenure';
    case 'EQUITY_EXIT':
      return 'Equity exit';
    default:
      return kind;
  }
}

const DeploymentTab: React.FC<{
  summary: CapitalAnalyticsSummary | null;
  rows: PartnerCapitalDeployment[] | null;
  useFilter: 'ALL' | 'LENDING' | 'EQUITY';
  onUseFilter: (v: 'ALL' | 'LENDING' | 'EQUITY') => void;
}> = ({ summary, rows, useFilter, onUseFilter }) => {
  const filtered = useMemo(() => {
    if (!rows) return [];
    if (useFilter === 'ALL') return rows;
    return rows.filter((r) => r.deploymentUse === useFilter);
  }, [rows, useFilter]);

  return (
    <Stack spacing={3}>
      {summary && (
        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label="Partner capital collected"
              value={fmtCompact(summary.totalPartnerCapital)}
              caption="Across all active subscriptions"
              accent="primary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label="Deployed to lending"
              value={fmtCompact(summary.deployedToLending)}
              caption={`${summary.avgPartnerSharePctMonthly}% to partner · ${summary.avgOperatorMarginPctMonthly}% operator`}
              accent="warning"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label="Deployed to equity"
              value={fmtCompact(summary.deployedToEquity)}
              caption="Profit captured on exit / MTM"
              accent="success"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label="Idle (undeployed)"
              value={fmtCompact(summary.idleCapital)}
              caption="Awaiting allocation to lend or invest"
            />
          </Grid>
        </Grid>
      )}

      <Card>
        <CardContent>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Capital flow model
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Partner money → lend or invest
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
                  You collect capital from partners. For lending you charge outsiders{' '}
                  <strong>{summary?.avgLendingSpreadPctMonthly ?? 2}%/month</strong>, pass{' '}
                  <strong>{summary?.avgPartnerSharePctMonthly ?? 1.5}%</strong> to the partner, and retain{' '}
                  <strong>{summary?.avgOperatorMarginPctMonthly ?? 0.5}%</strong> as operator margin.
                  For equity you deploy into stocks and capture profit on exit.
                </Typography>
              </Box>
              <TextField
                select
                size="small"
                label="Use"
                value={useFilter}
                onChange={(e) => onUseFilter(e.target.value as typeof useFilter)}
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="ALL">All deployments</MenuItem>
                <MenuItem value="LENDING">Lending only</MenuItem>
                <MenuItem value="EQUITY">Equity only</MenuItem>
              </TextField>
            </Stack>

            <Divider />

            {!rows ? (
              <LinearProgress />
            ) : filtered.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>No deployments match this filter</Typography>
            ) : (
              <Stack spacing={2}>
                {filtered.map((row) => (
                  <DeploymentFlowCard key={row.id} row={row} />
                ))}
              </Stack>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
};

const DeploymentFlowCard: React.FC<{ row: PartnerCapitalDeployment }> = ({ row }) => {
  const isLending = row.deploymentUse === 'LENDING';
  const monthlyPartnerIncome =
    isLending && row.capitalDeployed && row.partnerSharePctMonthly
      ? (row.capitalDeployed * row.partnerSharePctMonthly) / 100
      : 0;
  const monthlyOperatorMargin =
    isLending && row.capitalDeployed && row.operatorMarginPctMonthly
      ? (row.capitalDeployed * row.operatorMarginPctMonthly) / 100
      : 0;

  return (
    <Box
      sx={{
        p: 2.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'background.default',
      }}
    >
      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems={{ lg: 'center' }}>
        {/* Partner capital */}
        <Stack spacing={0.5} sx={{ minWidth: 160 }}>
          <Typography variant="caption" color="text.secondary">Partner capital</Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {row.partnerName}
          </Typography>
          <Typography variant="body2">
            {fmtCompact(row.capitalContributed)} contributed
          </Typography>
          <Chip label={row.schemeCode} size="small" variant="outlined" sx={{ width: 'fit-content' }} />
        </Stack>

        <ArrowForwardOutlined sx={{ color: 'text.disabled', display: { xs: 'none', lg: 'block' } }} />

        {/* Deployment target */}
        <Stack spacing={0.5} sx={{ flex: 1, minWidth: 200 }}>
          <Typography variant="caption" color="text.secondary">
            {isLending ? 'Lent to outsider' : 'Invested in'}
          </Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {isLending ? row.borrowerName : row.targetLabel}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isLending
              ? `${row.borrowerCity} · ${fmtCompact(row.capitalDeployed)} principal`
              : `${row.nseCode} · ${fmtCompact(row.capitalDeployed)} deployed`}
          </Typography>
        </Stack>

        <ArrowForwardOutlined sx={{ color: 'text.disabled', display: { xs: 'none', lg: 'block' } }} />

        {/* Economics */}
        <Stack spacing={0.5} sx={{ minWidth: 220 }}>
          {isLending ? (
            <>
              <Typography variant="caption" color="text.secondary">Monthly economics</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip size="small" label={`Borrower pays ${row.borrowerRatePctMonthly}%/mo`} color="default" />
                <Chip size="small" label={`Partner ${row.partnerSharePctMonthly}%`} color="success" variant="outlined" />
                <Chip size="small" label={`You ${row.operatorMarginPctMonthly}%`} color="primary" variant="outlined" />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                ≈ {fmt(monthlyPartnerIncome)}/mo to partner · {fmt(monthlyOperatorMargin)}/mo operator margin
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="caption" color="text.secondary">Equity P&amp;L</Typography>
              <Typography variant="body2">
                Entry {fmtCompact(row.entryValue ?? 0)} → Now {fmtCompact(row.currentValue ?? 0)}
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: (row.unrealizedPnl ?? 0) >= 0 ? 'success.main' : 'error.main' }}
              >
                Unrealised {(row.unrealizedPnl ?? 0) >= 0 ? '+' : ''}{fmtCompact(row.unrealizedPnl ?? 0)}
                {(row.realizedPnl ?? 0) > 0 && ` · Realised +${fmtCompact(row.realizedPnl ?? 0)}`}
              </Typography>
            </>
          )}
        </Stack>

        <Stack alignItems="flex-end" spacing={0.5} sx={{ minWidth: 100 }}>
          <Chip
            label={row.status}
            size="small"
            color={row.status === 'WATCH' ? 'warning' : 'success'}
            variant="outlined"
          />
          <Typography variant="caption" color="text.secondary">
            Matures {new Date(row.maturityDate).toLocaleDateString('en-IN')}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
};

const MarginReportTab: React.FC<{ periods: MarginPeriod[] | null; granularity: MarginGranularity; onGranularity: (g: MarginGranularity) => void }> = ({
  periods,
  granularity,
  onGranularity,
}) => {
  const chartData = useMemo(
    () =>
      (periods ?? []).map((p) => ({
        name: p.label,
        operatorMargin: p.netOperatorMargin,
        partnerPayouts: p.totalPartnerPayouts,
        lendingSpread: p.lendingOperatorMargin,
        equityProfit: p.equityRealizedProfit + p.equityUnrealizedPnl,
      })),
    [periods],
  );

  const totals = useMemo(() => {
    if (!periods?.length) return null;
    return periods.reduce(
      (acc, p) => ({
        netOperatorMargin: acc.netOperatorMargin + p.netOperatorMargin,
        totalPartnerPayouts: acc.totalPartnerPayouts + p.totalPartnerPayouts,
        lendingOperatorMargin: acc.lendingOperatorMargin + p.lendingOperatorMargin,
        equityRealizedProfit: acc.equityRealizedProfit + p.equityRealizedProfit,
      }),
      { netOperatorMargin: 0, totalPartnerPayouts: 0, lendingOperatorMargin: 0, equityRealizedProfit: 0 },
    );
  }, [periods]);

  return (
    <Stack spacing={3}>
      {totals && (
        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label="Net operator margin"
              value={fmtCompact(totals.netOperatorMargin)}
              caption={`Across ${periods?.length ?? 0} ${granularity === 'QUARTER' ? 'quarters' : 'months'}`}
              accent="primary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label="Paid to partners"
              value={fmtCompact(totals.totalPartnerPayouts)}
              caption="Lending share + equity distributions"
              accent="success"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label="Lending spread (yours)"
              value={fmtCompact(totals.lendingOperatorMargin)}
              caption="0.5% operator slice on deployed lending"
              accent="warning"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label="Equity realised"
              value={fmtCompact(totals.equityRealizedProfit)}
              caption="Booked on exits in period"
              accent="info"
            />
          </Grid>
        </Grid>
      )}

      <Card>
        <CardContent>
          <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
              <Box>
                <Typography variant="overline" color="text.secondary">Reporting</Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Margin over time</Typography>
              </Box>
              <Tabs
                value={granularity}
                onChange={(_, v) => onGranularity(v)}
                sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5 } }}
              >
                <Tab label="Monthly" value="MONTH" />
                <Tab label="Quarterly" value="QUARTER" />
              </Tabs>
            </Stack>
            <Divider />
            {!periods ? (
              <LinearProgress />
            ) : (
              <>
                <Box sx={{ width: '100%', height: 320 }}>
                  <ResponsiveContainer>
                    <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis tickFormatter={(v) => fmtCompact(v)} width={72} tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v: number) => fmt(v)} />
                      <Legend />
                      <Bar dataKey="operatorMargin" name="Net operator margin" fill="#1a237e" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="partnerPayouts" name="Partner payouts" fill="#15803d" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
                <Box sx={{ width: '100%', height: 260 }}>
                  <ResponsiveContainer>
                    <LineChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis tickFormatter={(v) => fmtCompact(v)} width={72} tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v: number) => fmt(v)} />
                      <Legend />
                      <Line type="monotone" dataKey="lendingSpread" name="Lending spread (operator)" stroke="#b45309" strokeWidth={2} dot />
                      <Line type="monotone" dataKey="equityProfit" name="Equity P&L" stroke="#1e40af" strokeWidth={2} dot />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Period</TableCell>
                        <TableCell align="right">Avg deployed</TableCell>
                        <TableCell align="right">Lending (operator)</TableCell>
                        <TableCell align="right">Lending (partner)</TableCell>
                        <TableCell align="right">Equity realised</TableCell>
                        <TableCell align="right">Equity unrealised</TableCell>
                        <TableCell align="right">Net margin</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {periods.map((p) => (
                        <TableRow key={p.periodKey}>
                          <TableCell sx={{ fontWeight: 600 }}>{p.label}</TableCell>
                          <TableCell align="right">{fmtCompact(p.avgDeployedCapital)}</TableCell>
                          <TableCell align="right">{fmtCompact(p.lendingOperatorMargin)}</TableCell>
                          <TableCell align="right">{fmtCompact(p.lendingPartnerShare)}</TableCell>
                          <TableCell align="right">{fmtCompact(p.equityRealizedProfit)}</TableCell>
                          <TableCell align="right">{fmtCompact(p.equityUnrealizedPnl)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtCompact(p.netOperatorMargin)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
};

const MaturityWarningsTab: React.FC<{
  warnings: MaturityWarning[] | null;
  summary: CapitalAnalyticsSummary | null;
  severityFilter: MaturitySeverity | 'ALL';
  onSeverityFilter: (v: MaturitySeverity | 'ALL') => void;
}> = ({ warnings, summary, severityFilter, onSeverityFilter }) => {
  const filtered = useMemo(() => {
    if (!warnings) return [];
    if (severityFilter === 'ALL') return warnings;
    return warnings.filter((w) => w.severity === severityFilter);
  }, [warnings, severityFilter]);

  return (
    <Stack spacing={3}>
      {summary && (
        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={4}>
            <StatCard
              label="Critical (< 14 days)"
              value={summary.maturityCriticalCount}
              caption="Immediate action required"
              accent="error"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <StatCard
              label="Warning (< 45 days)"
              value={summary.maturityWarningCount}
              caption="Plan rollover or exit"
              accent="warning"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <StatCard
              label="Total watchlist"
              value={warnings?.length ?? 0}
              caption="Lending debits, lock-ins, scheme & equity exits"
              accent="info"
            />
          </Grid>
        </Grid>
      )}

      <Card>
        <CardContent>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
              <Box>
                <Typography variant="overline" color="text.secondary">Capitals</Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Maturity date warnings</Typography>
                <Typography variant="body2" color="text.secondary">
                  Lending note maturities, subscription lock-ins, scheme tenures and planned equity exits.
                </Typography>
              </Box>
              <TextField
                select
                size="small"
                label="Severity"
                value={severityFilter}
                onChange={(e) => onSeverityFilter(e.target.value as MaturitySeverity | 'ALL')}
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="ALL">All severities</MenuItem>
                <MenuItem value="CRITICAL">Critical</MenuItem>
                <MenuItem value="WARNING">Warning</MenuItem>
                <MenuItem value="UPCOMING">Upcoming</MenuItem>
              </TextField>
            </Stack>
            <Divider />
            {!warnings ? (
              <LinearProgress />
            ) : filtered.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>No warnings in this bucket</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Severity</TableCell>
                      <TableCell>Partner</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Item</TableCell>
                      <TableCell>Maturity</TableCell>
                      <TableCell align="right">Days left</TableCell>
                      <TableCell align="right">Capital</TableCell>
                      <TableCell>Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filtered.map((w) => (
                      <TableRow key={w.id} hover>
                        <TableCell>
                          <Chip
                            label={w.severity}
                            size="small"
                            color={
                              w.severity === 'CRITICAL'
                                ? 'error'
                                : w.severity === 'WARNING'
                                  ? 'warning'
                                  : 'default'
                            }
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {w.partnerName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={kindLabel(w.kind)} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{w.label}</Typography>
                          <Typography
                            component={RouterLink}
                            to={STOCK_PICKS_PATH}
                            variant="caption"
                            sx={{ color: 'text.secondary', textDecoration: 'none' }}
                          >
                            {w.schemeCode}
                          </Typography>
                        </TableCell>
                        <TableCell>{new Date(w.maturityDate).toLocaleDateString('en-IN')}</TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color: w.daysRemaining <= 14 ? 'error.main' : w.daysRemaining <= 45 ? 'warning.main' : 'text.primary',
                            }}
                          >
                            {w.daysRemaining}d
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{fmtCompact(w.capitalAtRisk)}</TableCell>
                        <TableCell sx={{ maxWidth: 280 }}>
                          <Typography variant="caption" color="text.secondary">{w.details}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
};

const CapitalsAnalyticsHub: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [summary, setSummary] = useState<CapitalAnalyticsSummary | null>(null);
  const [deployments, setDeployments] = useState<PartnerCapitalDeployment[] | null>(null);
  const [marginPeriods, setMarginPeriods] = useState<MarginPeriod[] | null>(null);
  const [warnings, setWarnings] = useState<MaturityWarning[] | null>(null);
  const [useFilter, setUseFilter] = useState<'ALL' | 'LENDING' | 'EQUITY'>('ALL');
  const [marginGranularity, setMarginGranularity] = useState<MarginGranularity>('MONTH');
  const [severityFilter, setSeverityFilter] = useState<MaturitySeverity | 'ALL'>('ALL');

  useEffect(() => {
    getCapitalAnalyticsSummary().then(setSummary);
    listPartnerCapitalDeployments().then(setDeployments);
    listCapitalMaturityWarnings().then(setWarnings);
  }, []);

  useEffect(() => {
    setMarginPeriods(null);
    listCapitalMarginPeriods(marginGranularity).then(setMarginPeriods);
  }, [marginGranularity]);

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="overline" color="primary.main" sx={{ letterSpacing: '0.12em' }}>Capitals</Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>Partner capital deployment & reporting</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 720 }}>
          Track how partner money is lent to outsiders (2% → 1.5% partner / 0.5% you) or invested in stocks, with margin and maturity visibility.
        </Typography>
      </Box>

      <Alert severity="info" variant="outlined" sx={{ mb: 3 }}>
        Lending economics: borrower pays debit rate (typically 2%/month), partner receives 1.5%, operator retains the spread.
      </Alert>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab icon={<AccountBalanceOutlined fontSize="small" />} iconPosition="start" label={TAB_LABELS[0]} />
        <Tab icon={<TrendingUpOutlined fontSize="small" />} iconPosition="start" label={TAB_LABELS[1]} />
        <Tab
          icon={<WarningAmberOutlined fontSize="small" />}
          iconPosition="start"
          label={
            <Stack direction="row" spacing={1} alignItems="center">
              <span>{TAB_LABELS[2]}</span>
              {summary && summary.maturityCriticalCount > 0 && (
                <Chip label={summary.maturityCriticalCount} size="small" color="error" sx={{ height: 20, fontSize: 11 }} />
              )}
            </Stack>
          }
        />
      </Tabs>

      {tab === 0 && (
        <DeploymentTab
          summary={summary}
          rows={deployments}
          useFilter={useFilter}
          onUseFilter={setUseFilter}
        />
      )}
      {tab === 1 && (
        <MarginReportTab
          periods={marginPeriods}
          granularity={marginGranularity}
          onGranularity={setMarginGranularity}
        />
      )}
      {tab === 2 && (
        <MaturityWarningsTab
          warnings={warnings}
          summary={summary}
          severityFilter={severityFilter}
          onSeverityFilter={setSeverityFilter}
        />
      )}
    </Box>
  );
};

export default CapitalsAnalyticsHub;
